import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Users, X } from 'lucide-react';
import ShareRunSheet from './ShareRunSheet';

/**
 * HostFillBanner - Banner shown to host when run is ≥70% full
 * 
 * "Almost full. Invite players to fill the last spots."
 * [Invite Players] button
 */
const HostFillBanner = ({ run, isHost }) => {
  const [dismissed, setDismissed] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  
  if (!run || !isHost || dismissed) return null;
  
  // Calculate fill percentage
  const playerCount = (run.team_a_players?.length || 0) + (run.team_b_players?.length || 0);
  const maxPlayers = run.max_players || 10;
  const fillPercent = (playerCount / maxPlayers) * 100;
  const spotsLeft = maxPlayers - playerCount;
  
  // Only show if ≥70% full and not already full
  if (fillPercent < 70 || spotsLeft <= 0) return null;
  
  // Don't show for cancelled/completed games
  if (run.status === 'cancelled' || run.status === 'completed') return null;
  
  return (
    <>
      <div 
        className="relative mb-4 p-4 bg-amber-50 rounded-xl border border-amber-100"
        data-testid="host-fill-banner"
      >
        {/* Dismiss button */}
        <button
          onClick={() => setDismissed(true)}
          className="absolute right-2 top-2 p-1 rounded-full hover:bg-amber-100 transition-colors"
          data-testid="dismiss-fill-banner"
        >
          <X className="w-4 h-4 text-amber-400" />
        </button>
        
        <div className="flex items-center gap-3 pr-6">
          <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
            <Users className="w-5 h-5 text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-amber-900">
              Almost full
            </p>
            <p className="text-sm text-amber-700">
              Invite players to fill the last {spotsLeft} {spotsLeft === 1 ? 'spot' : 'spots'}.
            </p>
          </div>
        </div>
        
        <Button
          onClick={() => setShareOpen(true)}
          className="w-full mt-3 h-10 rounded-xl font-semibold bg-amber-500 hover:bg-amber-600 text-white"
          data-testid="fill-banner-invite-btn"
        >
          Invite Players
        </Button>
      </div>
      
      <ShareRunSheet 
        open={shareOpen} 
        onOpenChange={setShareOpen} 
        run={run}
        source="host_banner"
      />
    </>
  );
};

export default HostFillBanner;
